import { useEffect, useMemo, useState } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { Link } from "react-router-dom";
import { formatEther } from "viem";
import {
  RiGlobalLine,
  RiLoader4Line,
  RiLockLine,
  RiNodeTree,
  RiServerLine,
  RiStackLine,
  RiWallet3Line,
} from "@remixicon/react";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/card";
import { Separator } from "@/components/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/sidebar";
import { SILO_NODE_ABI, SILO_NODE_CONTRACT } from "@/abis/silo-node";
import { LRSNODE_V3_CONTRACT_CONFIG } from "@/configs/lrsnode.config";
import { mainConfig } from "@/configs/main.config";
import { publicClient } from "@/configs/client";
import { useSilo, useStats } from "@/providers/global-data.provider";

type SiloLeaderboardNode = {
  nodeId: string;
  providerStake: string;
  totalDelegated: string;
  stats?: {
    lastContactSuccessAt?: string | null;
  } | null;
};

type SiloNodeSnapshot = {
  activeNodes: number | null;
  totalLeaderboardNodes: number | null;
  /** `totalNodes()` on SILO staking contract when available */
  registeredNodesOnChain: bigint | null;
  lrsStaked: bigint;
  /** `getTotalActiveDelegators()` — distinct addresses with non-zero delegation */
  activeDelegators: bigint | null;
  isLoading: boolean;
  error: string | null;
};

type LrsNodeSnapshot = {
  activeProKey: bigint | null;
  activeLiteKey: bigint | null;
  isLoading: boolean;
  error: string | null;
};

const NODE_LEADERBOARD_API_URL =
  import.meta.env.VITE_SILO_NODES_API_URL || "http://127.0.0.1:3006/api/nodes";

const EMPTY_SILO_NODE_SNAPSHOT: SiloNodeSnapshot = {
  activeNodes: null,
  totalLeaderboardNodes: null,
  registeredNodesOnChain: null,
  lrsStaked: 0n,
  activeDelegators: null,
  isLoading: true,
  error: null,
};

const EMPTY_LRSNODE_SNAPSHOT: LrsNodeSnapshot = {
  activeProKey: null,
  activeLiteKey: null,
  isLoading: true,
  error: null,
};

function formatUsd(value: number, options?: Intl.NumberFormatOptions) {
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    ...options,
  });
}

function formatCompactNumber(value: number, options?: Intl.NumberFormatOptions) {
  return value.toLocaleString(undefined, {
    notation: "compact",
    maximumFractionDigits: 2,
    ...options,
  });
}

function formatLrs(value: number, maximumFractionDigits = 4) {
  return `${value.toLocaleString(undefined, { maximumFractionDigits })} LRS`;
}

function formatBigintLrs(value: bigint, maximumFractionDigits = 4) {
  return formatLrs(Number(formatEther(value)), maximumFractionDigits);
}

function formatCountriesSummary(
  countries: Array<{ countryCode: string; nodes: number }>,
  maxItems: number,
) {
  if (!countries.length) return "Unavailable";
  return countries
    .slice(0, maxItems)
    .map((country) => `${mainConfig.countryCodeToName[country.countryCode] || country.countryCode} (${country.nodes})`)
    .join(", ");
}

function isRecentlySeen(timestamp?: string | null) {
  if (!timestamp) return false;
  const parsed = new Date(timestamp).getTime();
  if (!Number.isFinite(parsed)) return false;
  return Date.now() - parsed <= 60 * 60 * 1000;
}

function SectionMetric({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper?: string;
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-background/50 p-4">
      <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className="mt-2 text-xl font-semibold tracking-tight sm:text-2xl">{value}</div>
      {helper ? <div className="mt-1 text-sm text-muted-foreground">{helper}</div> : null}
    </div>
  );
}

function SectionPanel({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="overflow-hidden border-border/70 bg-card/95 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-semibold text-foreground">{title}</CardTitle>
            <CardDescription className="mt-2 text-sm leading-6">{description}</CardDescription>
          </div>
          <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
            {icon}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
      </CardContent>
    </Card>
  );
}

export default function BlockchainStatsPage() {
  const {
    activeWallets,
    price,
    marketCap,
    circulatingSupply,
    lrsNodes,
    totalValueLocked,
    tvlPercentage,
    isLoading,
    error,
  } = useStats();
  const { totalSpace, activeNodes: siloActiveNodes, countries, isLoading: siloIsLoading } = useSilo();
  const [siloNodeSnapshot, setSiloNodeSnapshot] = useState<SiloNodeSnapshot>(EMPTY_SILO_NODE_SNAPSHOT);
  const [lrsNodeSnapshot, setLrsNodeSnapshot] = useState<LrsNodeSnapshot>(EMPTY_LRSNODE_SNAPSHOT);

  const node3Ready =
    !!LRSNODE_V3_CONTRACT_CONFIG.address &&
    LRSNODE_V3_CONTRACT_CONFIG.address.length > 2;

  useEffect(() => {
    let mounted = true;

    const fetchLrsNodeSnapshot = async () => {
      if (!node3Ready) {
        if (!mounted) return;
        setLrsNodeSnapshot({
          activeProKey: null,
          activeLiteKey: null,
          isLoading: false,
          error: "Node3 contract is not configured",
        });
        return;
      }

      try {
        const [plan1, plan2] = await Promise.all([
          publicClient.readContract({
            address: LRSNODE_V3_CONTRACT_CONFIG.address,
            abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
            functionName: "plans",
            args: [1n],
          }) as Promise<readonly [bigint, bigint, bigint, bigint, boolean]>,
          publicClient.readContract({
            address: LRSNODE_V3_CONTRACT_CONFIG.address,
            abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
            functionName: "plans",
            args: [2n],
          }) as Promise<readonly [bigint, bigint, bigint, bigint, boolean]>,
        ]);

        if (!mounted) return;

        setLrsNodeSnapshot({
          activeProKey: plan1[3],
          activeLiteKey: plan2[3],
          isLoading: false,
          error: null,
        });
      } catch (snapshotError) {
        if (!mounted) return;
        setLrsNodeSnapshot({
          activeProKey: null,
          activeLiteKey: null,
          isLoading: false,
          error: snapshotError instanceof Error ? snapshotError.message : "Failed to load LRSNode plan stats",
        });
      }
    };

    void fetchLrsNodeSnapshot();

    return () => {
      mounted = false;
    };
  }, [node3Ready]);

  useEffect(() => {
    let mounted = true;

    const fetchSiloNodeSnapshot = async () => {
      let providerPrincipal = 0n;
      let delegatorPrincipal = 0n;
      let registeredOnChain: bigint | null = null;
      let activeLeaderboardNodes: number | null = null;
      let totalLeaderboardNodes: number | null = null;
      let contractError: string | null = null;
      let leaderboardError: string | null = null;
      let activeDelegatorsOnChain: bigint | null = null;

      if (SILO_NODE_CONTRACT) {
        try {
          const [p, d, n, totalActiveDelegators] = await Promise.all([
            publicClient.readContract({
              address: SILO_NODE_CONTRACT,
              abi: SILO_NODE_ABI,
              functionName: "totalProviderPrincipal",
            }),
            publicClient.readContract({
              address: SILO_NODE_CONTRACT,
              abi: SILO_NODE_ABI,
              functionName: "totalDelegatorPrincipal",
            }),
            publicClient
              .readContract({
                address: SILO_NODE_CONTRACT,
                abi: SILO_NODE_ABI,
                functionName: "totalNodes",
              })
              .catch(() => null),
            publicClient
              .readContract({
                address: SILO_NODE_CONTRACT,
                abi: SILO_NODE_ABI,
                functionName: "getTotalActiveDelegators",
              })
              .catch(() => null),
          ]);
          providerPrincipal = p as bigint;
          delegatorPrincipal = d as bigint;
          registeredOnChain = typeof n === "bigint" ? n : null;
          activeDelegatorsOnChain =
            typeof totalActiveDelegators === "bigint" ? totalActiveDelegators : null;
        } catch {
          contractError = "SILO staking contract reads failed";
        }
      }

      try {
        const leaderboardResponse = await fetch(NODE_LEADERBOARD_API_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (!leaderboardResponse.ok) {
          leaderboardError = `SILO leaderboard load failed: ${leaderboardResponse.status}`;
        } else {
          const payload = await leaderboardResponse.json();
          const nodes = ((payload?.data || []) as SiloLeaderboardNode[]).filter(Boolean);
          activeLeaderboardNodes = nodes.filter((item) => isRecentlySeen(item.stats?.lastContactSuccessAt)).length;
          totalLeaderboardNodes = nodes.length;
        }
      } catch {
        leaderboardError = "SILO leaderboard load failed (network or parse error)";
      }

      if (!mounted) return;

      const partialError = [contractError, leaderboardError].filter(Boolean).join(" · ") || null;

      setSiloNodeSnapshot({
        activeNodes: activeLeaderboardNodes,
        totalLeaderboardNodes,
        registeredNodesOnChain: registeredOnChain,
        lrsStaked: providerPrincipal + delegatorPrincipal,
        activeDelegators: activeDelegatorsOnChain,
        isLoading: false,
        error: partialError,
      });
    };

    void fetchSiloNodeSnapshot();

    return () => {
      mounted = false;
    };
  }, []);

  const topCountries = useMemo(() => formatCountriesSummary(countries, 3), [countries]);
  const statsReady = !isLoading && !error;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="mx-auto w-full max-w-7xl">
            <header className="flex min-h-20 shrink-0 flex-wrap items-center gap-3 border-b py-4 transition-all ease-linear">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Stats</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6 md:py-8">
              <div className="mb-8 overflow-hidden rounded-3xl border border-border/70 bg-gradient-to-br from-card via-card to-primary/5">
                <div className="grid gap-8 px-6 py-8 md:px-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:items-end">
                  <div>
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      <RiStackLine className="size-4" />
                      Consolidated network overview
                    </div>
                    <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Larissa Statistics</h1>
                    <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground sm:text-base">
                      One operational snapshot for the Larissa blockchain, LRSNodes, and SILO node network. Separate stats pages are hidden so this page becomes the single source of truth.
                    </p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                    <div className="rounded-2xl border border-border/70 bg-background/60 p-4">
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Blockchain</div>
                      <div className="mt-2 text-2xl font-semibold">{statsReady ? formatCompactNumber(activeWallets) : "—"}</div>
                      <div className="mt-1 text-sm text-muted-foreground">active wallets</div>
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-background/60 p-4">
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">LRSNodes</div>
                      <div className="mt-2 text-2xl font-semibold">{statsReady ? lrsNodes.toLocaleString() : "—"}</div>
                      <div className="mt-1 text-sm text-muted-foreground">active nodes</div>
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-background/60 p-4">
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">SILO Node</div>
                      <div className="mt-2 text-2xl font-semibold">
                        {siloNodeSnapshot.isLoading
                          ? "—"
                          : siloNodeSnapshot.registeredNodesOnChain !== null
                            ? siloNodeSnapshot.registeredNodesOnChain.toLocaleString()
                            : (siloNodeSnapshot.activeNodes ?? siloActiveNodes).toLocaleString()}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {siloNodeSnapshot.registeredNodesOnChain !== null
                          ? "registered"
                          : "active nodes"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {(isLoading || siloNodeSnapshot.isLoading || lrsNodeSnapshot.isLoading || siloIsLoading) && (
                <div className="flex flex-col items-center justify-center py-12">
                  <RiLoader4Line className="mb-4 size-10 animate-spin text-primary" />
                  <p className="text-muted-foreground">Loading Larissa statistics...</p>
                </div>
              )}

              {error && !isLoading && (
                <Card className="mb-6 border-red-500/30 bg-red-500/5">
                  <CardContent className="p-5 text-sm text-red-300">
                    Error loading blockchain statistics: {error}
                  </CardContent>
                </Card>
              )}

              {!isLoading && !error && (
                <div className="space-y-6">
                  <SectionPanel
                    title="Blockchain"
                    description="Core network and token supply metrics for the main Larissa chain."
                    icon={<RiWallet3Line className="size-5" />}
                  >
                    <SectionMetric label="Active Wallets" value={activeWallets.toLocaleString()} />
                    <SectionMetric label="Price" value={formatUsd(price, { maximumFractionDigits: 6 })} />
                    <SectionMetric
                      label="Market Cap"
                      value={formatUsd(marketCap, {
                        notation: "compact",
                        maximumFractionDigits: 2,
                      })}
                    />
                    <SectionMetric
                      label="Circulating Supply"
                      value={circulatingSupply.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      helper="LRS in circulation"
                    />
                    <SectionMetric
                      label="Locked Circulating Supply"
                      value={formatLrs(totalValueLocked, 0)}
                      helper={`${tvlPercentage.toFixed(1)}% of circulating supply`}
                    />
                  </SectionPanel>

                  <SectionPanel
                    title="LRSNodes"
                    description="High-level node issuance metrics consolidated from the current LRSNode data source."
                    icon={<RiNodeTree className="size-5" />}
                  >
                    <SectionMetric
                      label="Active Nodes"
                      value={lrsNodes.toLocaleString()}
                      helper="Tracked from Node3 issued keys"
                    />
                    <SectionMetric
                      label="Active Pro Key"
                      value={lrsNodeSnapshot.isLoading ? "Loading..." : `${lrsNodeSnapshot.activeProKey?.toString() ?? "—"}`}
                      helper="Plan ID 1"
                    />
                    <SectionMetric
                      label="Active Lite Key"
                      value={lrsNodeSnapshot.isLoading ? "Loading..." : `${lrsNodeSnapshot.activeLiteKey?.toString() ?? "—"}`}
                      helper="Plan ID 2"
                    />
                  </SectionPanel>

                  <SectionPanel
                    title="SILO Node"
                    description="Operational node activity, geographic distribution, network capacity, and on-chain stake locked for SILO."
                    icon={<RiServerLine className="size-5" />}
                  >
                    <SectionMetric
                      label="Active Nodes"
                      value={
                        siloNodeSnapshot.isLoading
                          ? "Loading..."
                          : `${(siloNodeSnapshot.activeNodes ?? siloActiveNodes).toLocaleString()}`
                      }
                      helper={
                        siloNodeSnapshot.totalLeaderboardNodes
                          ? `${siloNodeSnapshot.totalLeaderboardNodes.toLocaleString()} leaderboard nodes observed`
                          : "Uses recent heartbeat activity where available"
                      }
                    />
                    <SectionMetric
                      label="Active delegators"
                      value={
                        siloNodeSnapshot.isLoading
                          ? "Loading..."
                          : siloNodeSnapshot.activeDelegators !== null
                            ? siloNodeSnapshot.activeDelegators.toLocaleString()
                            : "—"
                      }
                      helper={
                        SILO_NODE_CONTRACT
                          ? "Total active delegators"
                          : "Set VITE_SILO_NODE_CONTRACT_ADDRESS"
                      }
                    />
                    <SectionMetric
                      label="Total Space"
                      value={`${totalSpace.toFixed(2)} TB`}
                      helper="Aggregated storage capacity"
                    />
                    <SectionMetric
                      label="Countries"
                      value={countries.length ? countries.length.toLocaleString() : "0"}
                      helper={topCountries}
                    />
                    <SectionMetric
                      label="LRS Staked"
                      value={
                        siloNodeSnapshot.isLoading
                          ? "Loading..."
                          : formatBigintLrs(siloNodeSnapshot.lrsStaked, 4)
                      }
                      helper="Provider principal + delegator principal"
                    />
                  </SectionPanel>

                  {siloNodeSnapshot.error && (
                    <Card className="border-amber-500/30 bg-amber-500/5">
                      <CardContent className="p-5 text-sm text-amber-200">
                        SILO node activity details are partially unavailable: {siloNodeSnapshot.error}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
